import { timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import CancelToken from './cancelToken';
import { AuthenticatorType } from './espb/authenticator_type_pb';
import { CredentialService } from './espb/credential_service_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  AuthenticatorId,
  Email,
  LoginChallengeData,
  LoginData,
  UserId,
  OrganizationId,
  SyncUpdate,
  SessionType,
  LoginLongPollChannelChallengeResponse,
  AuditlogAdminToken,
  AuditlogWriteToken,
  LFDOverridesAdminToken,
  SyncToken,
} from './types';

export default class CredentialServiceClient {
  constructor(private readonly espbClient: Client<typeof CredentialService>) {}

  async createChallenge(
    email: Email | undefined,
    authenticatorId: AuthenticatorId | undefined,
    userId: UserId | undefined,
  ): Promise<LoginChallengeData> {
    const resp = await this.espbClient.createChallenge({
      email,
      userId,
      authenticatorId,
    });

    return {
      userId: requireFieldNotEmpty(resp.userId),
      challenge: requireFieldNotEmpty(resp.challenge),
      authenticators: resp.authenticators.map((a) => ({
        id: requireFieldNotEmpty(a.id),
        authenticatorType: knownEnumOrDefault(a.authenticatorType, AuthenticatorType),
        secretInfo: a.secretInfo,
        webauthn:
          a.webauthn ?
            {
              webauthnId: requireFieldNotEmpty(a.webauthn.webauthnId),
              prfSalt: requireFieldNotEmpty(a.webauthn.prfSalt),
            }
          : undefined,
      })),
    };
  }

  async createLongPollChannelChallenge(
    publicKeyHash: string,
    cancelToken: CancelToken,
  ): Promise<LoginLongPollChannelChallengeResponse> {
    const resp = await this.espbClient.createLongPollChannelChallenge(
      { publicKeyHash },
      { signal: cancelToken.signal },
    );
    const auth = requireFieldNotEmpty(resp.authenticator);

    return {
      userId: requireFieldNotEmpty(resp.userId),
      challenge: requireFieldNotEmpty(resp.challenge),
      authenticator: {
        id: auth.id,
        authenticatorType: knownEnumOrDefault(auth.authenticatorType, AuthenticatorType),
        secretInfo: auth.secretInfo,
      },
      authenticatorReply: requireFieldNotEmpty(resp.authenticatorReply),
    };
  }

  async completeLongPollChannel(
    publicKeyHash: string,
    authenticatorId: AuthenticatorId,
    authenticatorReply: Uint8Array,
  ): Promise<void> {
    await this.espbClient.completeLongPollChannel({
      publicKeyHash,
      authenticatorId,
      authenticatorReply,
    });
  }

  async createTokens(
    userId: UserId,
    authenticatorId: AuthenticatorId,
    challenge: string,
    responseData: Uint8Array,
    sessionUnlock:
      | {
          encryptedSecret: Uint8Array;
          maxExpiresAt: Date;
          singleUse: boolean;
        }
      | undefined,
    sessionType: SessionType,
    browserManagedDeviceToken?: string,
  ): Promise<{
    loginData: LoginData;
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.createTokens({
      authenticatorId,
      challenge,
      response: responseData,
      sessionType,
      browserManagedDeviceToken,
      sessionUnlock:
        sessionUnlock ?
          { ...sessionUnlock, maxExpiresAt: timestampFromDate(sessionUnlock.maxExpiresAt) }
        : undefined,
    });
    const token = requireFieldNotEmpty(resp.accessToken);
    const syncUpdate = toSyncDto(requireFieldNotEmpty(resp.syncUpdate));

    const loginData = {
      userId,
      sessionId: requireFieldNotEmpty(resp.sessionId),
      token: requireFieldNotEmpty(token.token),
      tokenVersion: resp.tokenVersion,
    };

    return { loginData, syncUpdate };
  }

  async createAuditlogWriteToken(organizationId: OrganizationId): Promise<AuditlogWriteToken> {
    const resp = await this.espbClient.createAuditlogWriteToken({ organizationId });
    const token = requireFieldNotEmpty(resp.token);
    return {
      token: requireFieldNotEmpty(token.token),
      expiresAt: timestampDate(requireFieldNotEmpty(token.expiresAt)),
    };
  }

  async createAuditlogAdminToken(
    organizationId: OrganizationId,
    cancelToken?: CancelToken,
  ): Promise<AuditlogAdminToken> {
    const resp = await this.espbClient.createAuditlogAdminToken(
      { organizationId },
      { signal: cancelToken?.signal },
    );
    const token = requireFieldNotEmpty(resp.token);
    return {
      token: requireFieldNotEmpty(token.token),
      expiresAt: timestampDate(requireFieldNotEmpty(token.expiresAt)),
    };
  }

  async createLFDOverridesAdminToken(): Promise<LFDOverridesAdminToken> {
    const resp = await this.espbClient.createLFDOverridesAdminToken({});
    const token = requireFieldNotEmpty(resp.token);
    return {
      token: requireFieldNotEmpty(token.token),
      expiresAt: timestampDate(requireFieldNotEmpty(token.expiresAt)),
    };
  }

  async createSyncToken(): Promise<SyncToken> {
    const resp = await this.espbClient.createSyncToken({});
    const token = requireFieldNotEmpty(resp.token);
    return {
      token: requireFieldNotEmpty(token.token),
      expiresAt: timestampDate(requireFieldNotEmpty(token.expiresAt)),
    };
  }

  async refreshToken(): Promise<{ token: string; tokenVersion: number }> {
    const resp = await this.espbClient.refreshToken({});
    const token = requireFieldNotEmpty(resp.newAccessToken);
    return { token: token.token, tokenVersion: resp.tokenVersion };
  }
}
